import { ref, computed } from "vue";

export type Branch = number[];

export function useExcludedBranch() {
	const excludedBranchesMap = ref<Set<string>>(new Set());
	const excludedBranchesArray = computed(() =>
		Array.from(excludedBranchesMap.value)
	);

	const excludedBranches = computed((): Branch[] =>
		Array.from(excludedBranchesMap.value).map((eb) => JSON.parse(eb))
	);

	const convert = (branch: Branch) => JSON.stringify(branch);

	const deleteBranch = (branch: Branch) => {
		excludedBranchesMap.value.delete(convert(branch));
	};

	const deleteBranchAndChildren = (branch: Branch) => {
		const branchStart = convert(branch).replace("]", ",");

		deleteBranch(branch);
		excludedBranchesArray.value
			.filter((branch) => branch.startsWith(branchStart))
			.forEach((branch) => excludedBranchesMap.value.delete(branch));
	};

	const toggleBranch = (branch: Branch) => {
		const converted = convert(branch);

		if (excludedBranchesMap.value.has(converted)) {
			deleteBranchAndChildren(branch);
		} else {
			excludedBranchesMap.value.add(converted);
		}
	};

	const setBranch = (branch: Branch, value: boolean, recursive = false) => {
		const converted = convert(branch);

		if (value) {
			excludedBranchesMap.value.add(converted);
		} else {
			if (recursive) {
				deleteBranchAndChildren(branch);
			} else {
				deleteBranch(branch);
			}
		}
	};

	const isBranchExcluded = (branch: Branch) =>
		excludedBranchesMap.value.has(convert(branch));

	const isBranchIndeterminate = (branch: Branch) => {
		if (isBranchExcluded(branch)) return false;

		const branchStart = convert(branch).replace("]", ",");

		return excludedBranchesArray.value.some((branch: string) =>
			branch.startsWith(branchStart)
		);
	};

	const clearBranches = () => excludedBranchesMap.value.clear();

	return {
		excludedBranches,
		toggleBranch,
		setBranch,
		isBranchExcluded,
		isBranchIndeterminate,
		clearBranches,
	};
}
